/**
 * FDA Orange Book Therapeutic Equivalence (TE) code labels — issue #805.
 * Source: FDA Orange Book Preface, 46th Edition (current as of 2026-01-15)
 * https://www.fda.gov/drugs/development-approval-process-drugs/orange-book-preface
 *
 * Mirrors the dmfTypeLabels.ts pattern used for USDMF status: one shared
 * lookup consumed by every surface that renders `te_code`
 * (OrangeBookPanel.tsx, externalLinkRecordAdapter.ts's consuming
 * ExternalLinkReviewList.tsx, and LinkedProductsPanel.tsx).
 */
export const ORANGE_BOOK_PREFACE_URL =
  'https://www.fda.gov/drugs/development-approval-process-drugs/orange-book-preface';

export interface TeCodeInfo {
  shortName: string;
  fullDescription: string;
}

export const TE_CODE_LABELS: Record<string, TeCodeInfo> = {
  AA: {
    shortName: 'Conventional dosage forms',
    fullDescription:
      'Multisource drug products coded AA contain active ingredients and are in dosage forms that are not regarded as presenting either actual or potential bioequivalence problems or drug quality or standards issues.',
  },
  AB: {
    shortName: 'Bioequivalence requirements met',
    fullDescription:
      'Multisource drug products listed under the same heading and having the same strength generally will be coded AB if data and information are submitted demonstrating bioequivalence. A number is added to make a three-character code (AB1, AB2, AB3, etc.) when more than one Reference Listed Drug (RLD) of the same strength has been designated under the same heading.',
  },
  AN: {
    shortName: 'Solutions/powders for aerosolization',
    fullDescription:
      'Solutions and powders intended for aerosolization that are marketed for use in general-use delivery systems are considered pharmaceutically and therapeutically equivalent and coded AN. Products compatible only with a specific delivery system are coded BN unless they meet an appropriate bioequivalence standard.',
  },
  AO: {
    shortName: 'Injectable oil solutions',
    fullDescription:
      'Injectable oil solutions are considered pharmaceutically and therapeutically equivalent only when the active ingredient, its concentration, and the type of oil used as a vehicle are all identical.',
  },
  AP: {
    shortName: 'Injectable aqueous solutions',
    fullDescription:
      'Injectable products are available as solutions or powders for reconstitution; these are considered different dosage forms and thus not pharmaceutically equivalent to each other.',
  },
  AT: {
    shortName: 'Topical products',
    fullDescription:
      'Solutions and Drug Efficacy Study Implementation (DESI) drug products in the same topical dosage form for which an in vivo bioequivalence waiver was granted, or for which in vitro evidence establishes bioequivalence and chemistry/manufacturing processes are adequate, are considered therapeutically equivalent and coded AT.',
  },
  'B*': {
    shortName: 'Requires further FDA investigation',
    fullDescription:
      'Assigned to products previously coded A or B when FDA receives new information raising a significant question about therapeutic equivalence that can only be resolved through further Agency investigation/review.',
  },
  BC: {
    shortName: 'Extended-release, no bioequivalence data',
    fullDescription:
      'Extended-release products (capsules, injectables, tablets) for which bioequivalence data (rate and extent) have not been submitted are coded BC; those with such data are coded AB.',
  },
  BD: {
    shortName: 'Documented bioequivalence problems',
    fullDescription:
      'Denotes products with active ingredients that have known bioequivalence problems and for which adequate studies have not been submitted; once submitted, the product is coded AB.',
  },
  BE: {
    shortName: 'Delayed-release oral, unresolved',
    fullDescription:
      'Delayed-release products containing the same active ingredient are presumed to present a potential bioequivalence problem and coded BE absent in vivo studies; with adequate in vivo studies, coded AB.',
  },
  BN: {
    shortName: 'Aerosol-nebulizer, device-specific',
    fullDescription:
      'Applies to drug solutions/powders marketed only as a component of, or compatible with, a specific drug delivery system; not considered therapeutically equivalent unless an appropriate bioequivalence standard is met (then coded AB).',
  },
  BP: {
    shortName: 'Potential bioequivalence problem',
    fullDescription:
      'Oral dosage forms are coded BP until adequate bioequivalence data are submitted (then AB). Injectable suspensions in an aqueous or oleaginous vehicle have also been coded BP.',
  },
  BR: {
    shortName: 'Suppositories/enemas, no in vivo evidence',
    fullDescription:
      'Systemic suppositories or enemas are considered bioequivalent only with in vivo evidence; coded AB if evidence is available, BR if not.',
  },
  BS: {
    shortName: 'Drug standard deficiencies',
    fullDescription:
      'If FDA finds the drug standards for an active ingredient/dosage form deficient enough to prevent evaluation of pharmaceutical or therapeutic equivalence, all products with that ingredient/form are coded BS.',
  },
  BT: {
    shortName: 'Topical, bioequivalence issues',
    fullDescription:
      'Mainly post-1962 dermatologic/ophthalmic/otic/rectal/vaginal topical products not intended for systemic absorption; coded BT if not bioequivalent to other pharmaceutically equivalent products or lacking sufficient evidence.',
  },
  BX: {
    shortName: 'Insufficient data',
    fullDescription:
      'Assigned when data reviewed by FDA are insufficient to determine therapeutic equivalence; products are presumed therapeutically inequivalent until adequate information is available.',
  },
};

/**
 * Resolves one TE sub-code to its label info. Handles the AB1/AB2/AB3...
 * strength-disambiguation suffix (shares AB's description per the Orange
 * Book Preface) and falls back to the B* wildcard entry for any other
 * B-prefixed code not itemized above. Returns `undefined` for anything else
 * unrecognized -- callers still show a tooltip for that case (flagging the
 * code as unrecognized with a link to the Orange Book Preface, per issue
 * #805 follow-up feedback), just not one of the descriptions in this file.
 */
export function getTeCodeInfo(code: string): TeCodeInfo | undefined {
  const trimmed = code.trim().toUpperCase();
  if (!trimmed) return undefined;
  if (TE_CODE_LABELS[trimmed]) return TE_CODE_LABELS[trimmed];
  if (/^AB\d+$/.test(trimmed)) return TE_CODE_LABELS.AB;
  if (/^B/.test(trimmed)) return TE_CODE_LABELS['B*'];
  return undefined;
}

/**
 * Splits a raw, possibly-combined `te_code` value (e.g. `"AB1,AB2,AB3"`)
 * into its individual sub-codes so each one resolves independently instead
 * of being left as an unmapped blob (issue #805 acceptance criteria).
 */
export function parseTeCodeParts(raw: string): string[] {
  return raw
    .split(',')
    .map(part => part.trim())
    .filter(part => part.length > 0);
}

/** `"[code] - [Short name]"`, or the raw code if unrecognized. */
export function formatTeCodePart(code: string): string {
  const info = getTeCodeInfo(code);
  return info ? `${code} - ${info.shortName}` : code;
}
